import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import { queries } from "@/api";
import { ErrorNote } from "@/components/ErrorNote";
import { FleetTable } from "@/components/FleetTable";
import { SignInNeeded } from "@/components/SignInNeeded";

type Scope = "public" | "member" | "mine";
type MarketSort = "updated" | "flown" | "hours" | "status";
const SORTS: MarketSort[] = ["updated", "flown", "hours", "status"];
const CONTINENTS: [string, string][] = [
  ["", "Every continent"],
  ["EU", "Europe"],
  ["NA", "North America"],
  ["SA", "South America"],
  ["AS", "Asia"],
  ["OC", "Oceania"],
  ["AF", "Africa"],
  ["AN", "Antarctica"],
];

/** A continent code from the address, whatever its case; empty for all. */
function continentOf(value: unknown): string {
  const code = typeof value === "string" ? value.toUpperCase() : "";
  return CONTINENTS.some(([c]) => c === code) ? code : "";
}

export const Route = createFileRoute("/airframes/")({
  // The Marketplace (decision 071): the airframes shared by others that you
  // are not part of yet, by default; a scope in the address is accepted and ignored.
  validateSearch: (search: Record<string, unknown>): { scope: Scope; q: string; joined: "exclude" | "include"; continent: string; sort: MarketSort } => ({
    scope: "public",
    q: typeof search.q === "string" ? search.q : "",
    joined: search.joined === "include" ? "include" : "exclude",
    continent: continentOf(search.continent),
    sort: SORTS.includes(search.sort as MarketSort) ? (search.sort as MarketSort) : "updated",
  }),
  component: AirframeList,
});

function AirframeList() {
  const { scope, q, joined, continent, sort } = Route.useSearch();
  const navigate = Route.useNavigate();
  const me = useQuery(queries.me());
  const [draft, setDraft] = useState(q);
  // The URL is the truth: a navigation (sidebar link, back) resets the box.
  useEffect(() => setDraft(q), [q]);
  // The airframes are for the pilots (F415): nothing is asked for before the session is known.
  const list = useQuery({ ...queries.airframes(scope, q, { joined, continent, sort }), enabled: !!me.data });
  if (me.isPending) return null;
  if (me.error) return <ErrorNote error={me.error} />;
  if (!me.data) return <SignInNeeded />;
  return (
    <div className="space-y-5">
      <header className="flex flex-wrap items-center gap-3">
        <div>
          <h1 className="page-title">Marketplace</h1>
          <p className="muted text-sm">{list.data ? `${list.data.total} airframe${list.data.total === 1 ? "" : "s"}${joined === "include" ? " shared on the platform, yours among them" : " shared by others that you are not part of yet"}` : " "}</p>
        </div>
        <form
          className="ml-auto flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            navigate({ search: { scope, q: draft.trim(), joined, continent, sort } });
          }}
        >
          <input
            className="input"
            type="search"
            placeholder="Registration, name, livery or MSN"
            aria-label="Search"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            data-testid="search"
            style={{ width: 280 }}
          />
          <button type="submit" className="btn">
            Search
          </button>
        </form>
      </header>
      <div className="flex flex-wrap items-center gap-3 text-sm" data-testid="market-filters">
        <label className="flex items-center gap-1.5">
          <input
            type="checkbox"
            checked={joined === "include"}
            onChange={(e) => navigate({ search: { scope, q, joined: e.target.checked ? "include" : "exclude", continent, sort } })}
            data-testid="market-joined"
          />
          Show the airframes you are part of already
        </label>
        <select className="input" value={continent} onChange={(e) => navigate({ search: { scope, q, joined, continent: e.target.value, sort } })} aria-label="Continent" data-testid="market-continent">
          {CONTINENTS.map(([code, name]) => (
            <option key={code || "all"} value={code}>
              {name}
            </option>
          ))}
        </select>
        <select className="input" value={sort} onChange={(e) => navigate({ search: { scope, q, joined, continent, sort: e.target.value as MarketSort } })} aria-label="Sort by" data-testid="market-sort">
          <option value="updated">Latest shared or returned</option>
          <option value="flown">Latest flown</option>
          <option value="hours">Most hours</option>
          <option value="status">Available first</option>
        </select>
      </div>
      <ErrorNote error={list.error} />
      {list.data ? (
        list.data.items.length === 0 ? (
          <p className="muted" data-testid="empty">
            No shared airframe matches. The ones you fly are on the Fleet{joined === "include" ? "" : "; tick the box to see the ones you are part of here too"}.
          </p>
        ) : (
          <section className="card-flush">
            <FleetTable items={list.data.items} mode="find" />
          </section>
        )
      ) : null}
    </div>
  );
}
